import React, { useEffect, useState } from "react";
import CvcInfoIcon from "../../../assets/icons/cvc-info-icon.svg";
import { getCardBrandMeta } from "../constants/cardBrand";

const toApiExpiryDate = (value) => {
  const cleaned = String(value || "").replace(/\s+/g, "");
  const matched = cleaned.match(/^(\d{2})\/(\d{2})$/);
  if (!matched) return null;

  const partA = Number(matched[1]);
  const partB = Number(matched[2]);

  // Prefer MM/YY when first part is a valid month
  if (partA >= 1 && partA <= 12) {
    const month = partA;
    const year = 2000 + partB;
    return `${year}-${String(month).padStart(2, "0")}-01`;
  }

  // If first part looks like a day and second is a valid month (e.g. 28/12),
  // interpret as DD/MM -> year from the first part
  if (partB >= 1 && partB <= 12) {
    const month = partB;
    const year = 2000 + partA;
    return `${year}-${String(month).padStart(2, "0")}-01`;
  }

  return null;
};

const formatCardNumberInput = (value) => {
  const digitsOnly = String(value || "").replace(/\D/g, "").slice(0, 19);
  return digitsOnly.replace(/(.{4})/g, "$1 ").trim();
};

const formatExpiryInput = (value) => {
  const digitsOnly = String(value || "").replace(/\D/g, "").slice(0, 4);
  if (digitsOnly.length <= 2) {
    return digitsOnly;
  }
  return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
};

export default function AddPaymentModal({
  open,
  onClose,
  onAdd,
  isSubmitting = false,
  errorMessage = "",
}) {
  const [cardNumber, setCardNumber] = useState("");
  const [exp, setExp] = useState("");
  const [cvc, setCvc] = useState("");
  const [name, setName] = useState("");
  const [formError, setFormError] = useState("");
  const [visibleError, setVisibleError] = useState("");

  const detectedBrand = getCardBrandMeta({ cardNumber });

  useEffect(() => {
    const nextError = formError || errorMessage;
    if (!nextError) {
      setVisibleError("");
      return;
    }

    setVisibleError(nextError);
    const timeoutId = setTimeout(() => {
      setVisibleError("");
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [formError, errorMessage]);

  if (!open) return null;

  const handleAdd = async () => {
    setFormError("");

    const trimmedNumber = cardNumber.replace(/\s+/g, "");
    if (trimmedNumber.length < 12 || !/^\d+$/.test(trimmedNumber)) {
      setFormError("Please enter a valid card number.");
      return;
    }

    const expiryDate = toApiExpiryDate(exp);
    if (!expiryDate) {
      setFormError("Please use expiration date in MM/YY format.");
      return;
    }

    if (cvc.length < 3 || !/^\d+$/.test(cvc)) {
      setFormError("Please enter a valid security code.");
      return;
    }

    if (!name.trim()) {
      setFormError("Cardholder name is required.");
      return;
    }

    const payload = {
      card_number: trimmedNumber,
      cardholder_name: name.trim(),
      security_code: cvc,
      expiry_date: expiryDate,
      is_default: true,
      card_status: "active",
    };

    const result = await onAdd(payload);
    if (result?.error) {
      return;
    }

    setCardNumber("");
    setExp("");
    setCvc("");
    setName("");
    setFormError("");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center max-sm:pt-5">
      <div className="absolute inset-0 bg-black opacity-50" onClick={onClose} />
      <div className="relative w-[1000px] bg-white rounded-2xl p-8 border border-line mx-6 sm:mx-10">
        <h3 className="text-xl font-semibold mb-4">Add Payment Method</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="max-sm:col-span-1 max-lg:col-span-2">
            <label className="text-sm text-primary mb-1 block">
              Card Number
            </label>
            <div className="flex items-center border border-primary rounded-md px-3 py-2">
              <input
                className="flex-1 outline-none placeholder:text-primary text-sm sm:text-base"
                placeholder="xxx  xxx  xxx  xxx"
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumberInput(e.target.value))}
              />
              <div className="ml-3 sm:flex hidden items-center gap-2">
                <img
                  src={detectedBrand.icon}
                  alt={detectedBrand.label}
                  className="h-4 sm:h-6"
                  fetchpriority="high"
                  loading="eager"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm text-primary mb-1 block">
              Expiration Date
            </label>
            <input
              className="w-full rounded-md px-3 py-2 border border-primary placeholder:text-primary text-sm sm:text-base"
              placeholder="MM/YY"
              value={exp}
              type="text"
              inputMode="numeric"
              maxLength={5}
              onChange={(e) => setExp(formatExpiryInput(e.target.value))}
            />
          </div>

          <div>
            <label className="text-sm text-primary mb-1 block">
              Security Code
            </label>
            <div className="flex items-center border border-primary rounded-md px-3 py-2">
              <input
                className="flex-1 outline-none placeholder:text-primary text-sm sm:text-base"
                placeholder="CVC"
                value={cvc}
                onChange={(e) => setCvc(e.target.value)}
                type="text"
              />
              <img src={CvcInfoIcon} alt="CVC icon" />
            </div>
          </div>

          <div className="max-sm:col-span-1 max-lg:col-span-2">
            <label className="text-sm text-primary mb-1 block">
              Cardholder name
            </label>
            <input
              className="w-full border border-primary rounded-md px-3 py-2 placeholder:text-primary text-sm sm:text-base"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>

        {visibleError && (
          <p className="mt-4 text-sm text-red-600">{visibleError}</p>
        )}

        <div className="mt-6 flex justify-end gap-3 font-semibold">
          <button
            onClick={handleAdd}
            disabled={isSubmitting}
            className="bg-primary text-white text-sm sm:text-base px-4 py-2 rounded-md leading-none"
          >
            {isSubmitting ? "ADDING..." : "ADD CARD"}
          </button>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="border border-primary rounded-md px-4 py-2 text-primary text-sm sm:text-base leading-none"
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}
